export const OPS_BY_FAMILY = {
  string: [
    'eq',
    'neq',
    'in',
    'not_in',
    'contains',
    'not_contains',
    'starts_with',
    'ends_with',
    'is_empty',
    'is_not_empty',
    'is_null',
    'is_not_null',
  ],
  numeric: ['eq', 'neq', 'in', 'not_in', 'lt', 'lte', 'gt', 'gte', 'between', 'is_null', 'is_not_null'],
  date: ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'between', 'in_last', 'older_than', 'is_null', 'is_not_null'],
  timestamp: [
    'eq',
    'neq',
    'lt',
    'lte',
    'gt',
    'gte',
    'between',
    'in_last',
    'older_than',
    'is_null',
    'is_not_null',
  ],
  boolean: ['is_null', 'is_not_null', 'is_true', 'is_not_true'],
  array: ['array_contains', 'in', 'not_in', 'is_null', 'is_not_null'],
};

/** Plain-language operator labels, keyed by type family (falls back to `default`). */
export const OP_LABELS_BY_FAMILY = {
  default: {
    eq: 'is',
    neq: 'is not',
    in: 'is any of',
    not_in: 'is not any of',
    lt: 'is less than',
    lte: 'is at most',
    gt: 'is more than',
    gte: 'is at least',
    between: 'is between',
    in_last: 'is in the last',
    // Reads as a menu choice; `describeCondition` shortens it for a sentence, where the
    // amount follows ("is at least 30 days old").
    older_than: 'is at least … old',
    contains: 'contains',
    not_contains: 'does not contain',
    starts_with: 'starts with',
    ends_with: 'ends with',
    is_empty: 'is empty',
    is_not_empty: 'is not empty',
    is_null: 'is missing',
    is_not_null: 'is present',
    is_true: 'is true',
    is_not_true: 'is not true',
    array_contains: 'includes',
  },
  date: {
    eq: 'is on',
    neq: 'is not on',
    lt: 'is before',
    lte: 'is on or before',
    gt: 'is after',
    gte: 'is on or after',
  },
  timestamp: {
    eq: 'is at',
    neq: 'is not at',
    lt: 'is before',
    lte: 'is at or before',
    gt: 'is after',
    gte: 'is at or after',
  },
  array: {
    eq: 'includes',
    neq: 'does not include',
    in: 'includes any of',
    not_in: 'includes none of',
  },
};

/** Flat label map (legacy name) — default-family labels. */
export const OP_LABELS = OP_LABELS_BY_FAMILY.default;

export function opLabel(op, columnType) {
  const fam = typeFamily(columnType);
  const famLabels = OP_LABELS_BY_FAMILY[fam] || {};
  return famLabels[op] || OP_LABELS_BY_FAMILY.default[op] || op;
}

/** Short symbolic label for compact summaries. */
export const OP_SYMBOLS = {
  eq: '=',
  neq: '≠',
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
};

export const RELATIVE_DATE_UNITS = [
  { value: 'days', label: 'days' },
  { value: 'months', label: 'months' },
  { value: 'years', label: 'years' },
];

export function typeFamily(columnType) {
  const t = String(columnType || '').trim().toLowerCase();
  if (!t) return 'string';
  if (t.startsWith('array')) return 'array';
  if (t === 'boolean' || t === 'bool') return 'boolean';
  if (t === 'date') return 'date';
  if (t === 'timestamp' || t === 'timestamptz') return 'timestamp';
  if (
    ['bigint', 'int', 'integer', 'smallint', 'tinyint', 'float', 'double', 'decimal', 'numeric', 'long'].includes(t)
  ) {
    return 'numeric';
  }
  return 'string';
}

export const OPS_FOR_VALUE_LIST = ['in', 'not_in', 'eq', 'neq', 'is_null', 'is_not_null'];

/** Operators that stay meaningful for the derived fast_id_score column (0–10 score). */
export const OPS_FOR_FAST_ID_SCORE = ['eq', 'neq', 'gte', 'gt', 'lte', 'lt', 'between', 'is_null', 'is_not_null'];

export function isFastIdScoreColumn(condition) {
  return (
    String(condition?.column || '') === 'fast_id_score' &&
    ((condition?.schema === 'profile' && condition?.table === 'profile') ||
      (condition?.schema === 'signals' && condition?.table === 'last_signals_gold'))
  );
}

export function operatorsForType(columnType) {
  const fam = typeFamily(columnType);
  return OPS_BY_FAMILY[fam] || OPS_BY_FAMILY.string;
}

export function operatorsForColumn(columnType, hasValueList, condition = null) {
  if (condition && isFastIdScoreColumn(condition)) return OPS_FOR_FAST_ID_SCORE;
  if (hasValueList) return OPS_FOR_VALUE_LIST;
  return operatorsForType(columnType);
}

/** Default operator when a column is first chosen. */
export function defaultOperatorForColumn(col) {
  if (col && isFastIdScoreColumn(col)) return 'gte';
  if (col?.has_value_list) return 'in';
  const fam = typeFamily(col?.column_type);
  if (fam === 'date' || fam === 'timestamp') return 'in_last';
  if (fam === 'boolean') return 'is_true';
  if (fam === 'array') return 'array_contains';
  return 'eq';
}

export function opNeedsNoValue(op) {
  return (
    op === 'is_null' ||
    op === 'is_not_null' ||
    op === 'is_true' ||
    op === 'is_not_true' ||
    op === 'is_empty' ||
    op === 'is_not_empty'
  );
}

export function opIsMulti(op) {
  return op === 'in' || op === 'not_in';
}

/** Parse a stored multi-value operand (JSON array preferred; comma/newline fallback). */
export function parseListValue(raw) {
  let parts;
  if (Array.isArray(raw)) {
    parts = raw;
  } else {
    const s = String(raw ?? '').trim();
    if (!s) return [];
    parts = null;
    if (s.startsWith('[')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) parts = parsed;
      } catch (e) {
        /* fall through to delimiter parsing */
      }
    }
    if (!parts) parts = s.includes('\n') ? s.split('\n') : s.split(',');
  }
  const out = [];
  for (const part of parts) {
    const v = String(part ?? '').trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

/**
 * Normalize a `/column-values` response into `[{ value, label }]`, where `label` is
 * `value_label` when the dictionary provides one and the raw value otherwise.
 */
export function normalizeColumnValues(data) {
  if (!Array.isArray(data)) return [];
  const out = [];
  for (const entry of data) {
    const isObject = entry && typeof entry === 'object';
    const value = String((isObject ? entry.value : entry) ?? '').trim();
    if (!value) continue;
    const label = String((isObject ? entry.value_label : '') ?? '').trim();
    out.push({ value, label: label || value });
  }
  return out;
}

/** Display text for a stored value: its label from the column's value list, else the value itself. */
export function columnValueLabel(value, options) {
  const match = (options || []).find((o) => o.value === value);
  return match ? match.label : String(value ?? '');
}

export function serializeListValue(values) {
  const list = (values || []).map((v) => String(v).trim()).filter(Boolean);
  return list.length ? JSON.stringify(list) : '';
}

/**
 * True for the operators measuring a period back from today rather than naming a date.
 * Both carry an amount in `value` and a unit in `value2`, so everything that asks —
 * the amount box, the unit menu, validation, the sentence — asks this one question.
 */
export function opIsRelativeDate(op) {
  return op === 'in_last' || op === 'older_than';
}

/** True when an SQL-preview error should be shown under Operator/Value instead of globally. */
export function isConditionValueError(message) {
  const msg = String(message || '');
  return (
    /value is required/i.test(msg) ||
    /requires a (non-empty )?value/i.test(msg) ||
    /requires two values/i.test(msg) ||
    /date or timestamp value is required/i.test(msg) ||
    /positive whole number/i.test(msg) ||
    /amount is too large/i.test(msg) ||
    /In the last/i.test(msg) ||
    /Invalid numeric literal/i.test(msg) ||
    /array_contains requires a value/i.test(msg)
  );
}

/**
 * Field-level value errors for a condition (mirrors segment SQL generation rules).
 * Returns `{ value?, value2? }` message strings when the condition is complete enough to validate.
 */
export function conditionFieldErrors(condition) {
  const errors = {};
  if (!condition?.catalog || !condition?.schema || !condition?.table || !condition?.column) {
    return errors;
  }

  const op = String(condition.operator || '').trim().toLowerCase();
  if (opNeedsNoValue(op)) return errors;

  const fam = typeFamily(condition.column_type);
  const v = String(condition.value ?? '').trim();
  const v2 = String(condition.value2 ?? '').trim();

  if (opIsMulti(op)) {
    const list = parseListValue(condition.value);
    if (!list.length) {
      errors.value = 'Choose at least one value.';
    } else if (fam === 'numeric') {
      const bad = list.find((x) => !/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(x));
      if (bad) errors.value = `Invalid numeric literal: '${bad}'`;
    }
    return errors;
  }

  if (isFastIdScoreColumn(condition)) {
    const okScore = (x) => /^\d+$/.test(x) && Number(x) >= 0 && Number(x) <= 10;
    if (!v || !okScore(v)) errors.value = 'Fast-ID score must be a whole number from 0 to 10.';
    if (op === 'between' && (!v2 || !okScore(v2))) errors.value2 = 'Fast-ID score must be a whole number from 0 to 10.';
    return errors;
  }

  if (op === 'between') {
    if (!v) errors.value = 'A value is required for this operator.';
    if (!v2) errors.value2 = 'A value is required for this operator.';
    if (v && fam === 'numeric' && !/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(v)) {
      errors.value = `Invalid numeric literal: '${condition.value}'`;
    }
    if (v2 && fam === 'numeric' && !/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(v2)) {
      errors.value2 = `Invalid numeric literal: '${condition.value2}'`;
    }
    return errors;
  }

  if (opIsRelativeDate(op)) {
    const label = op === 'in_last' ? 'In the last' : 'At least … ago';
    if (!/^\d+$/.test(v) || Number(v) < 1) {
      errors.value = `"${label}" requires a positive whole number (e.g. 30).`;
    } else if (Number(v) > 99999) {
      errors.value = `"${label}" amount is too large.`;
    }
    return errors;
  }

  if (fam === 'numeric') {
    if (!v) {
      errors.value = 'A value is required for this operator.';
    } else if (!/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(v)) {
      errors.value = `Invalid numeric literal: '${condition.value}'`;
    }
    return errors;
  }

  if (fam === 'date' || fam === 'timestamp') {
    if (!v) errors.value = 'A date or timestamp value is required.';
    return errors;
  }

  if (fam === 'array' && op === 'array_contains') {
    if (!v) errors.value = 'array_contains requires a value.';
    return errors;
  }

  if (['contains', 'not_contains', 'starts_with', 'ends_with'].includes(op)) {
    if (!v) errors.value = `Operator '${op}' requires a non-empty value.`;
    return errors;
  }

  // Plain string comparisons: an empty value is almost always a half-finished row.
  // Use "is empty" / "is not empty" to match blanks on purpose.
  if ((op === 'eq' || op === 'neq') && !v) {
    errors.value = 'Enter a value, or use “is empty”.';
  }

  return errors;
}

export function emptyCondition() {
  return {
    kind: 'condition',
    catalog: '',
    schema: '',
    table: '',
    column: '',
    column_type: '',
    has_value_list: false,
    display_name: '',
    operator: 'eq',
    value: '',
    value2: '',
    folder_list: [],
  };
}

export function emptyGroup() {
  return { kind: 'group', op: 'AND', children: [], sample_pct: 100, sample_seed: 0 };
}

export function defaultCriteriaRoot() {
  return { kind: 'group', op: 'AND', children: [], sample_pct: 100, sample_seed: 0 };
}

// ---- group percentage sampling -------------------------------------------
//
// Every group can keep a percentage of the profiles it matches. The backend
// turns that into a deterministic hash bucket on MD5, so the same people are
// picked on every count, export, and scheduled run until the seed changes.

/** A group sample can be narrow but never empty. */
export const MIN_SAMPLE_PCT = 0.01;
export const MAX_SAMPLE_SEED = 2147483647;

export function clampSamplePct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 100;
  const bounded = Math.min(100, Math.max(MIN_SAMPLE_PCT, n));
  return Math.round(bounded * 100) / 100;
}

export function groupSamplePct(group) {
  const raw = group?.sample_pct;
  if (raw == null || raw === '') return 100;
  return clampSamplePct(raw);
}

/** True when this group keeps only part of what it matches. */
export function isSampled(group) {
  return group?.kind === 'group' && groupSamplePct(group) < 100;
}

export function anyGroupSampled(root) {
  return collectGroupPaths(root).some(({ node }) => isSampled(node));
}

export function randomSampleSeed() {
  return 1 + Math.floor(Math.random() * (MAX_SAMPLE_SEED - 1));
}

/** Dot-joined child-index path the backend uses to key group counts ('' is the root). */
export function pathKey(path) {
  return (path || []).join('.');
}

/** Every group in the tree in pre-order, root first: `[{ path, key, node }]`. */
export function collectGroupPaths(root) {
  const out = [];
  const walk = (node, path) => {
    if (node?.kind !== 'group') return;
    out.push({ path, key: pathKey(path), node });
    (node.children || []).forEach((child, i) => walk(child, [...path, i]));
  };
  walk(root, []);
  return out;
}

/**
 * How many profiles a group contributes once its own percentage and every
 * sampled ancestor above it are taken into account.
 */
export function groupContributionEstimate(base, pct, ancestorFactor = 1) {
  if (base == null || base === '') return null;
  const n = Number(base);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * (clampSamplePct(pct) / 100) * (Number(ancestorFactor) || 1));
}

/** Set one group's percentage, assigning a shuffle seed the first time it drops below 100. */
export function setSamplePctAtPath(root, path, pct) {
  return updateNodeAtPath(root, path, (node) => {
    const next = clampSamplePct(pct);
    const seed = next < 100 && !node.sample_seed ? randomSampleSeed() : node.sample_seed || 0;
    return { ...node, sample_pct: next, sample_seed: seed };
  });
}

/** Pick a different sample of the same size for one group. */
export function reshuffleSampleAtPath(root, path) {
  return updateNodeAtPath(root, path, (node) => ({ ...node, sample_seed: randomSampleSeed() }));
}

function setAllSamplePct(root, pct) {
  let next = root;
  collectGroupPaths(root).forEach(({ path }) => {
    next = setSamplePctAtPath(next, path, pct);
  });
  return next;
}

/**
 * Back-solve the percentage sliders so the segment lands on `target` profiles.
 *
 * `groupCounts` comes from the last count and is keyed by `pathKey` — `''` is the
 * number of profiles the criteria match with no sampling at all. `currentTotal`
 * is the exact size the last count reported for the percentages in place now.
 *
 * Nothing sampled yet: set the root's percentage to `target / matching`.
 * Already sampling: scale every sampled group by the same factor, which keeps
 * the mix the user dialled in. Sampling is linear, so one re-count confirms it.
 */
export function fitToTarget(root, { groupCounts = {}, currentTotal = null, target } = {}) {
  const wanted = Number(target);
  if (!root || !Number.isFinite(wanted) || wanted <= 0) {
    return { criteria: root, ok: false, reason: 'Enter a target audience size.' };
  }
  const matching = Number(groupCounts[pathKey([])]);
  if (!Number.isFinite(matching) || matching <= 0) {
    return { criteria: root, ok: false, reason: 'Run a count first so group sizes are known.' };
  }
  if (wanted >= matching) {
    return {
      criteria: setAllSamplePct(root, 100),
      ok: true,
      reason: `These criteria only match ${matching.toLocaleString()} profiles — taking all of them.`,
    };
  }

  const sampled = collectGroupPaths(root).filter(({ node }) => isSampled(node));
  if (!sampled.length) {
    return { criteria: setSamplePctAtPath(root, [], (wanted / matching) * 100), ok: true, reason: '' };
  }

  const from = Number(currentTotal) > 0 ? Number(currentTotal) : matching;
  const factor = wanted / from;
  let next = root;
  let allMaxed = true;
  sampled.forEach(({ path, node }) => {
    const scaled = clampSamplePct(groupSamplePct(node) * factor);
    if (scaled < 100) allMaxed = false;
    next = setSamplePctAtPath(next, path, scaled);
  });

  return {
    criteria: next,
    ok: true,
    reason: allMaxed
      ? 'Every sampled group is now at 100% — that is the largest audience these criteria can reach.'
      : '',
  };
}

/**
 * Sampled groups whose percentage cannot add up, because a level above them combines
 * them with siblings using "All of". Under AND the audience is the intersection and the
 * percentages multiply, so the per-group estimates and the target solver would both lie.
 * Only the nearest level that actually combines siblings decides.
 */
export function samplingShapeIssues(root) {
  if (!root) return [];
  const issues = [];
  collectGroupPaths(root).forEach(({ path, key, node }) => {
    if (!isSampled(node) || !path.length) return;
    for (let i = path.length - 1; i >= 0; i -= 1) {
      const ancestorPath = path.slice(0, i);
      const ancestor = getNode(root, ancestorPath);
      if (!ancestor || (ancestor.children || []).length < 2) continue;
      if (ancestor.op === 'AND') {
        issues.push({
          path,
          key,
          pct: groupSamplePct(node),
          ancestorPath,
          ancestorKey: pathKey(ancestorPath),
        });
      }
      break;
    }
  });
  return issues;
}

/**
 * Move a group's percentage up to `ancestorPath`, which is where an "All of" level makes it
 * meaningful. The sample predicate hashes the profile md5 with the stored seed, so
 * `A AND (B AND hash < t)` and `(A AND B) AND hash < t` match exactly the same people:
 * carrying the seed up keeps the audience identical while putting the percentage where the
 * estimates and the solver can reason about it.
 */
export function hoistSampleToAncestor(root, groupPath, ancestorPath) {
  const group = getNode(root, groupPath);
  if (!isSampled(group)) return root;
  if (!isDescendantPath(groupPath, ancestorPath) || samePath(groupPath, ancestorPath)) return root;
  const ancestor = getNode(root, ancestorPath);
  if (ancestor?.kind !== 'group') return root;

  // An ancestor already taking a percentage keeps its own share of what arrives, so the
  // two multiply. That holds the size; only a single hoist keeps the very same profiles.
  const combined = clampSamplePct((groupSamplePct(ancestor) * groupSamplePct(group)) / 100);
  const seed = Number(ancestor.sample_seed) || Number(group.sample_seed) || randomSampleSeed();
  const cleared = updateNodeAtPath(root, groupPath, (n) => ({ ...n, sample_pct: 100, sample_seed: 0 }));
  return updateNodeAtPath(cleared, ancestorPath, (n) => ({ ...n, sample_pct: combined, sample_seed: seed }));
}

/** Set one group's and/or operator. */
export function setOpAtPath(root, path, op) {
  return updateNodeAtPath(root, path, (node) => ({ ...node, op: op === 'OR' ? 'OR' : 'AND' }));
}

/** True when criteria includes at least one condition with catalog/schema/table set. */
export function criteriaHasTableReferences(root) {
  if (!root) return false;
  if (root.kind === 'condition') {
    return Boolean(root.catalog && root.schema && root.table);
  }
  if (root.kind === 'group') {
    return (root.children || []).some(criteriaHasTableReferences);
  }
  return false;
}

export function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export function getNode(root, path) {
  let n = root;
  for (let i = 0; i < path.length; i += 1) {
    if (!n || n.kind !== 'group') return null;
    n = n.children[path[i]];
  }
  return n;
}

export function updateNodeAtPath(root, path, updater) {
  if (path.length === 0) {
    return updater(deepClone(root));
  }
  const next = deepClone(root);
  let parent = next;
  for (let i = 0; i < path.length - 1; i += 1) {
    parent = parent.children[path[i]];
  }
  const idx = path[path.length - 1];
  parent.children[idx] = updater(parent.children[idx]);
  return next;
}

export function removeAtPath(root, path) {
  if (path.length === 0) return root;
  const next = deepClone(root);
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  let parent = next;
  for (let i = 0; i < parentPath.length; i += 1) {
    parent = parent.children[parentPath[i]];
  }
  parent.children.splice(idx, 1);
  return next;
}

export function appendChildAtPath(root, path, child) {
  return updateNodeAtPath(root, path, (node) => ({
    ...node,
    children: [...(node.children || []), child],
  }));
}

/** Insert `child` into the group at `parentPath` at `index`. */
export function insertChildAtPath(root, parentPath, index, child) {
  return updateNodeAtPath(root, parentPath, (node) => {
    const children = [...(node.children || [])];
    const i = Math.max(0, Math.min(index, children.length));
    children.splice(i, 0, child);
    return { ...node, children };
  });
}

/** Duplicate the node at `path`, placing the copy right after it. */
export function duplicateAtPath(root, path) {
  if (!path.length) return root;
  const node = getNode(root, path);
  if (!node) return root;
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  return insertChildAtPath(root, parentPath, idx + 1, deepClone(node));
}

/** Wrap the node at `path` in a new group (so it can be combined with siblings using the other operator). */
export function wrapInGroupAtPath(root, path, op = 'OR') {
  if (!path.length) return root;
  return updateNodeAtPath(root, path, (node) => ({ ...emptyGroup(), op, children: [node] }));
}

/** True when the node at `path` sits inside a nested group and can be lifted out of it. */
export function canMoveOutOfGroup(path) {
  return Array.isArray(path) && path.length > 1;
}

/**
 * Percentage that taking the node at `path` out of its group would discard, or null
 * if nothing is lost. Removing the last child drops the group, and a percentage cannot
 * follow it: the branch would quietly go back to matching everyone, so the editor says so.
 */
export function samplePctLostByRemoving(root, path) {
  if (!Array.isArray(path) || path.length < 2) return null;
  const parent = getNode(root, path.slice(0, -1));
  if (!isSampled(parent) || (parent.children || []).length !== 1) return null;
  return groupSamplePct(parent);
}

/** Lift the node at `path` out of its group, placing it just after that group in the level above. */
export function moveOutOfGroupAtPath(root, path) {
  if (!canMoveOutOfGroup(path)) return root;
  const node = getNode(root, path);
  if (!node) return root;
  const parentPath = path.slice(0, -1);
  const grandParentPath = parentPath.slice(0, -1);
  const parentIdx = parentPath[parentPath.length - 1];

  let next = removeAtPath(root, path);
  const parent = getNode(next, parentPath);
  // The backend rejects empty groups, so a group whose last child just left is dropped
  // and the node takes its place.
  if (parent && !(parent.children || []).length) {
    next = removeAtPath(next, parentPath);
    return insertChildAtPath(next, grandParentPath, parentIdx, deepClone(node));
  }
  return insertChildAtPath(next, grandParentPath, parentIdx + 1, deepClone(node));
}

/** Move a child within its parent group by `delta` (-1 up, +1 down). */
export function moveWithinParent(root, path, delta) {
  if (!path.length) return root;
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  return updateNodeAtPath(root, parentPath, (node) => {
    const children = [...(node.children || [])];
    const to = idx + delta;
    if (to < 0 || to >= children.length) return node;
    const [item] = children.splice(idx, 1);
    children.splice(to, 0, item);
    return { ...node, children };
  });
}

/** True when `path` is `ancestor` itself or sits somewhere beneath it. */
export function isDescendantPath(path, ancestor) {
  if (!Array.isArray(path) || !Array.isArray(ancestor)) return false;
  if (path.length < ancestor.length) return false;
  return ancestor.every((idx, i) => path[i] === idx);
}

function samePath(a, b) {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]);
}

/**
 * Where `path` ends up once the node at `removed` is gone. Only siblings that sat
 * after the removed node shift, and only at that one level.
 */
function shiftPathAfterRemoval(path, removed) {
  if (!Array.isArray(path) || path.length < removed.length) return path;
  const level = removed.length - 1;
  for (let i = 0; i < level; i += 1) {
    if (path[i] !== removed[i]) return path;
  }
  if (path[level] > removed[level]) {
    const out = [...path];
    out[level] -= 1;
    return out;
  }
  return path;
}

/** Drop the group at `path` if the last child just left it, since the backend rejects empty groups. */
function dissolveIfEmptied(root, parentPath) {
  if (!parentPath.length) return { root, removed: null };
  const parent = getNode(root, parentPath);
  if (!parent || parent.kind !== 'group' || (parent.children || []).length) return { root, removed: null };
  return { root: removeAtPath(root, parentPath), removed: parentPath };
}

/**
 * Move the node at `fromPath` into the group at `toParentPath`, landing at `toIndex`
 * among that group's children. `toIndex` is read against the tree as it looks now, so
 * callers can pass the gap index they rendered.
 */
export function moveNodeToPath(root, fromPath, toParentPath, toIndex) {
  if (!Array.isArray(fromPath) || !fromPath.length) return root;
  // A group cannot be dropped into itself or into anything it contains.
  if (isDescendantPath(toParentPath, fromPath)) return root;
  const node = getNode(root, fromPath);
  const target = getNode(root, toParentPath);
  if (!node || target?.kind !== 'group') return root;

  const fromParentPath = fromPath.slice(0, -1);
  const fromIdx = fromPath[fromPath.length - 1];
  let index = Math.max(0, Math.min(Number(toIndex) || 0, (target.children || []).length));

  if (samePath(fromParentPath, toParentPath)) {
    // The gaps either side of the node are where it already is.
    if (index === fromIdx || index === fromIdx + 1) return root;
    if (index > fromIdx) index -= 1;
  }

  let next = removeAtPath(root, fromPath);
  let dest = shiftPathAfterRemoval(toParentPath, fromPath);
  const dissolved = dissolveIfEmptied(next, fromParentPath);
  next = dissolved.root;
  if (dissolved.removed) dest = shiftPathAfterRemoval(dest, dissolved.removed);

  return insertChildAtPath(next, dest, index, deepClone(node));
}

/**
 * Combine two siblings into a new group: the node at `targetPath` stays where it is and
 * the dragged node joins it. The group takes the operator its parent does not use, so
 * dropping one condition onto another under an "Any of" level builds the "All of" bucket
 * that percentage sampling adds up over.
 */
export function groupNodesAtPath(root, targetPath, draggedPath) {
  if (!Array.isArray(targetPath) || !Array.isArray(draggedPath) || !targetPath.length) return root;
  if (samePath(targetPath, draggedPath)) return root;
  // Neither node may contain the other, or it would end up inside itself.
  if (isDescendantPath(targetPath, draggedPath) || isDescendantPath(draggedPath, targetPath)) return root;
  const dragged = getNode(root, draggedPath);
  if (!dragged || !getNode(root, targetPath)) return root;

  let next = removeAtPath(root, draggedPath);
  let tPath = shiftPathAfterRemoval(targetPath, draggedPath);
  const dissolved = dissolveIfEmptied(next, draggedPath.slice(0, -1));
  next = dissolved.root;
  if (dissolved.removed) tPath = shiftPathAfterRemoval(tPath, dissolved.removed);

  const parent = getNode(next, tPath.slice(0, -1));
  const op = parent?.op === 'AND' ? 'OR' : 'AND';
  return updateNodeAtPath(next, tPath, (node) => ({
    ...emptyGroup(),
    op,
    children: [node, deepClone(dragged)],
  }));
}

/** True when a condition has a column picked. */
export function conditionHasColumn(condition) {
  return Boolean(condition?.catalog && condition?.schema && condition?.table && condition?.column);
}

/**
 * Whether the whole tree is ready to preview/count: at least one condition,
 * every condition has a column, and no field-level errors.
 */
export function criteriaIsComplete(root) {
  let count = 0;
  let ok = true;
  const walk = (n) => {
    if (!n || !ok) return;
    if (n.kind === 'condition') {
      count += 1;
      if (!conditionHasColumn(n)) {
        ok = false;
        return;
      }
      const errs = conditionFieldErrors(n);
      if (Object.keys(errs).length) ok = false;
      return;
    }
    // The backend rejects empty nested groups; the root may be empty only while count stays 0.
    if (!(n.children || []).length && n !== root) {
      ok = false;
      return;
    }
    (n.children || []).forEach(walk);
  };
  walk(root);
  return ok && count > 0;
}

/** Human-readable reason the tree is not ready, or '' when it is. */
export function criteriaIncompleteReason(root) {
  let reason = '';
  let count = 0;
  const walk = (n) => {
    if (!n || reason) return;
    if (n.kind === 'condition') {
      count += 1;
      if (!conditionHasColumn(n)) reason = 'Choose a column for every condition.';
      else {
        const errs = conditionFieldErrors(n);
        const first = errs.value || errs.value2;
        if (first) reason = first;
      }
      return;
    }
    if (!(n.children || []).length && n !== root) {
      reason = 'Remove empty groups or add a condition to them.';
      return;
    }
    (n.children || []).forEach(walk);
  };
  walk(root);
  if (!reason && count === 0) reason = 'Add at least one condition.';
  return reason;
}

/** Pick the column's human label from a condition or a search hit. */
export function columnLabel(colOrCondition) {
  return (
    colOrCondition?.display_name ||
    colOrCondition?.column_name ||
    colOrCondition?.column ||
    ''
  );
}

export function conditionDisplayLabel(condition) {
  if (condition.catalog && condition.schema && condition.table && condition.column) {
    return [condition.catalog, condition.schema, condition.table, condition.column].join('.');
  }
  return '';
}

const FRIENDLY_TABLE_DROP_TOKENS = new Set(['md5', 'flattened', 'last', 'gold']);

/** Turn a raw table name into a short Title Case label for typeahead UI. */
export function friendlyTableName(tableName) {
  const raw = String(tableName || '').trim();
  if (!raw) return '';
  return raw
    .split(/[_-]+/)
    .filter((token) => token && !FRIENDLY_TABLE_DROP_TOKENS.has(token.toLowerCase()))
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
}

// ---- several conditions on one column ------------------------------------
//
// Picking two values of one attribute — gender M and gender F — reads as "either" to
// the person clicking, but becomes unsatisfiable once each pick is its own condition
// under "All of". `in` / `not_in` say the same thing in one condition, so the offer
// report collapses picks on the way in and the editor offers the same fix when a
// hand-built tree runs into it.

/** What each mergeable operator becomes once several of them land on one column. */
const MULTI_OP_FOR = { eq: 'in', in: 'in', neq: 'not_in', not_in: 'not_in' };

/**
 * Which column a condition tests. Offer-report rules that have no profile column yet
 * fall back to the attribute they came from, so two picks on one attribute still meet
 * and the column only has to be chosen once.
 */
export function conditionColumnKey(condition) {
  if (condition?.kind !== 'condition') return '';
  if (conditionHasColumn(condition)) {
    return ['col', condition.catalog, condition.schema, condition.table, condition.column].join('\u0000');
  }
  const feature = String(condition?.offer_feature || '').trim();
  return feature ? `feature\u0000${feature}` : '';
}

/**
 * True when several conditions on this column can be restated as one multi-value
 * condition. Array columns are left alone: `in` asks whether the array holds any of
 * the values, so collapsing two "includes" would widen "has both" into "has either".
 */
function canMergeCondition(condition) {
  const op = String(condition?.operator || '').trim().toLowerCase();
  if (!MULTI_OP_FOR[op]) return false;
  if (typeFamily(condition?.column_type) === 'array') return false;
  return Boolean(conditionColumnKey(condition));
}

/** The values a mergeable condition tests, as a list. */
export function conditionValueList(condition) {
  const op = String(condition?.operator || '').trim().toLowerCase();
  // A single-value operand is taken whole: a comma in `eq` is part of the value.
  return opIsMulti(op) ? parseListValue(condition?.value) : parseListValue([condition?.value]);
}

/**
 * Key shared by conditions that would collapse into one, or '' when this condition
 * stands alone. The offer report's rules panel groups its rows by this too, so what it
 * lists and what the segment ends up with cannot drift apart.
 */
export function conditionMergeKey(condition) {
  if (!canMergeCondition(condition)) return '';
  return `${conditionColumnKey(condition)}\u0000${MULTI_OP_FOR[String(condition.operator).trim().toLowerCase()]}`;
}

/**
 * Collapse conditions testing the same column into one `is any of` / `is not any of`,
 * keeping the position of the first of each set. Nodes that cannot merge, and columns
 * carrying only one condition, come back untouched.
 *
 * Pass `onlyColumnKey` to leave every other column alone. Offer-report picks can all be
 * merged safely — two picks off one list mean "either" — but in the editor `state is TX`
 * beside `state is any of TX, OK` is an intersection somebody typed, and folding those
 * together would widen it.
 */
export function mergeSiblingConditions(nodes, onlyColumnKey = null) {
  const list = Array.isArray(nodes) ? nodes : [];
  const buckets = new Map();
  list.forEach((node, i) => {
    if (node?.kind !== 'condition') return;
    if (onlyColumnKey && conditionColumnKey(node) !== onlyColumnKey) return;
    const key = conditionMergeKey(node);
    if (!key) return;
    if (buckets.has(key)) buckets.get(key).push(i);
    else buckets.set(key, [i]);
  });

  const replacements = new Map();
  buckets.forEach((indexes, key) => {
    if (indexes.length < 2) return;
    const op = key.slice(key.lastIndexOf('\u0000') + 1);
    const values = [];
    indexes.forEach((i) => {
      conditionValueList(list[i]).forEach((v) => {
        if (!values.includes(v)) values.push(v);
      });
    });
    const merged = { ...list[indexes[0]], operator: op, value: serializeListValue(values), value2: '' };
    // These named one of the values; the condition now covers several.
    delete merged.offer_value;
    delete merged.offer_value_label;
    replacements.set(indexes[0], merged);
    indexes.slice(1).forEach((i) => replacements.set(i, null));
  });

  if (!replacements.size) return list;
  return list.map((node, i) => (replacements.has(i) ? replacements.get(i) : node)).filter(Boolean);
}

/** Collapse same-column conditions at every level of the tree. */
export function mergeConditionsInTree(node) {
  if (node?.kind !== 'group') return node;
  const children = (node.children || []).map(mergeConditionsInTree);
  return { ...node, children: mergeSiblingConditions(children) };
}

/**
 * Levels that cannot match anyone, because one column is asked for several values at
 * once or for a value it is also refused. Each entry carries the group's path and the
 * child indexes involved, plus whether restating them as one condition would fix it —
 * `gender is M and gender is not M` leaves nothing to combine.
 */
export function contradictionIssues(root) {
  if (!root) return [];
  const issues = [];
  collectGroupPaths(root).forEach(({ path, key, node }) => {
    const children = node.children || [];
    if (node.op !== 'AND' || children.length < 2) return;

    const byColumn = new Map();
    children.forEach((child, i) => {
      if (child?.kind !== 'condition' || !canMergeCondition(child)) return;
      const colKey = conditionColumnKey(child);
      if (byColumn.has(colKey)) byColumn.get(colKey).push(i);
      else byColumn.set(colKey, [i]);
    });

    byColumn.forEach((indexes, columnKey) => {
      if (indexes.length < 2) return;
      const demanded = [];
      const refused = new Set();
      indexes.forEach((i) => {
        const child = children[i];
        if (MULTI_OP_FOR[String(child.operator).trim().toLowerCase()] === 'in') {
          demanded.push(conditionValueList(child));
        } else {
          conditionValueList(child).forEach((v) => refused.add(v));
        }
      });
      // Only refusals stack up fine: "is not M and is not F" rules out two values.
      if (!demanded.length) return;
      const survivors = demanded
        .reduce((allowed, vals) => (allowed === null ? [...vals] : allowed.filter((v) => vals.includes(v))), null)
        .filter((v) => !refused.has(v));
      if (survivors.length) return;
      issues.push({
        path,
        key,
        columnKey,
        indexes,
        label: columnLabel(children[indexes[0]]) || 'this attribute',
        canMerge: new Set(indexes.map((i) => conditionMergeKey(children[i]))).size < indexes.length,
      });
    });
  });
  return issues;
}

/** Restate one column's competing conditions in the group at `path` as a single condition. */
export function mergeSameColumnAtPath(root, path, columnKey) {
  return updateNodeAtPath(root, path, (node) => ({
    ...node,
    children: mergeSiblingConditions(node.children || [], columnKey),
  }));
}

/** Base typeahead label: display name (or column name). */
export function columnOptionBaseLabel(hit) {
  return String(hit?.display_name || hit?.column_name || hit?.column || '—');
}

/**
 * Labels for a list of column hits. When multiple hits share the same base label
 * (e.g. two columns both named Domain on Profile), append the raw column name.
 */
export function columnOptionLabels(hits) {
  const list = Array.isArray(hits) ? hits : [];
  const bases = list.map(columnOptionBaseLabel);
  const counts = new Map();
  for (const base of bases) {
    counts.set(base, (counts.get(base) || 0) + 1);
  }
  return list.map((hit, i) => {
    const base = bases[i];
    if ((counts.get(base) || 0) < 2) return base;
    const col = String(hit?.column || '').trim();
    return col ? `${base} · ${col}` : base;
  });
}
